<script setup>
import { computed, onUnmounted } from 'vue';
import { useToggle } from '@vueuse/core';
import { useStore } from 'vuex';
import { useAlert } from 'dashboard/composables';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import { emitter } from 'shared/helpers/mitt';
import WebPhoneAPI from 'dashboard/api/webPhone';
import EmailTranscriptModal from './EmailTranscriptModal.vue';
import ResolveAction from '../../buttons/ResolveAction.vue';
import ButtonV4 from 'dashboard/components-next/button/Button.vue';
import DropdownMenu from 'dashboard/components-next/dropdown-menu/DropdownMenu.vue';
import { FEATURE_FLAGS } from 'dashboard/featureFlags';

import {
  CMD_MUTE_CONVERSATION,
  CMD_SEND_TRANSCRIPT,
  CMD_UNMUTE_CONVERSATION,
} from 'dashboard/helper/commandbar/events';

// No props needed as we're getting currentChat from the store directly
const store = useStore();
const { t } = useI18n();
const router = useRouter();

const [showEmailActionsModal, toggleEmailModal] = useToggle(false);
const [showActionsDropdown, toggleDropdown] = useToggle(false);

const currentChat = computed(() => store.getters.getSelectedChat);

// Get current contact to check if they have a phone number
const currentContact = computed(() => {
  const contactId = currentChat.value.meta?.sender?.id;
  return contactId ? store.getters['contacts/getContact'](contactId) : {};
});

// Check if contact has a phone number
const hasPhoneNumber = computed(() => {
  return (
    currentContact.value.phone_number &&
    currentContact.value.phone_number.trim() !== ''
  );
});

// Check if VOIP is configured
const isVoipConfigured = computed(() => {
  const currentUser = store.getters.getCurrentUser;
  const currentAccount = store.getters.getCurrentAccount;
  return !!(
    store.getters['accounts/isFeatureEnabledonAccount'](
      currentAccount?.id,
      FEATURE_FLAGS.WEB_PHONE
    ) &&
    currentUser?.voip_extension &&
    currentAccount?.voip_tenant_name &&
    currentAccount?.voip_tenant_key
  );
});

const actionMenuItems = computed(() => {
  const items = [];

  if (!currentChat.value.muted) {
    items.push({
      icon: 'i-lucide-volume-off',
      label: t('CONTACT_PANEL.MUTE_CONTACT'),
      action: 'mute',
      value: 'mute',
    });
  } else {
    items.push({
      icon: 'i-lucide-volume-1',
      label: t('CONTACT_PANEL.UNMUTE_CONTACT'),
      action: 'unmute',
      value: 'unmute',
    });
  }

  items.push({
    icon: 'i-lucide-share',
    label: t('CONTACT_PANEL.SEND_TRANSCRIPT'),
    action: 'send_transcript',
    value: 'send_transcript',
  });

  return items;
});

const handleActionClick = ({ action }) => {
  toggleDropdown(false);

  if (action === 'mute') {
    store.dispatch('muteConversation', currentChat.value.id);
    useAlert(t('CONTACT_PANEL.MUTED_SUCCESS'));
  } else if (action === 'unmute') {
    store.dispatch('unmuteConversation', currentChat.value.id);
    useAlert(t('CONTACT_PANEL.UNMUTED_SUCCESS'));
  } else if (action === 'send_transcript') {
    toggleEmailModal();
  }
};

// Handle call button click
const onClickToCall = async () => {
  try {
    await WebPhoneAPI.click_to_call({
      callee: currentContact.value.phone_number,
    });
    router.push({
      name: 'web_phone_home',
      params: { accountId: store.getters.getCurrentAccount.id },
    });
  } catch (error) {
    useAlert(
      error?.response?.data?.message
        ? error.response.data.message
        : 'Failed to initiate call'
    );
  }
};

// These functions are needed for the event listeners
const mute = () => {
  store.dispatch('muteConversation', currentChat.value.id);
  useAlert(t('CONTACT_PANEL.MUTED_SUCCESS'));
};

const unmute = () => {
  store.dispatch('unmuteConversation', currentChat.value.id);
  useAlert(t('CONTACT_PANEL.UNMUTED_SUCCESS'));
};

emitter.on(CMD_MUTE_CONVERSATION, mute);
emitter.on(CMD_UNMUTE_CONVERSATION, unmute);
emitter.on(CMD_SEND_TRANSCRIPT, toggleEmailModal);

onUnmounted(() => {
  emitter.off(CMD_MUTE_CONVERSATION, mute);
  emitter.off(CMD_UNMUTE_CONVERSATION, unmute);
  emitter.off(CMD_SEND_TRANSCRIPT, toggleEmailModal);
});
</script>

<template>
  <div class="relative flex items-center gap-2 actions--container">
    <ResolveAction
      :conversation-id="currentChat.id"
      :status="currentChat.status"
    />
    <ButtonV4
      v-if="hasPhoneNumber && isVoipConfigured"
      v-tooltip="$t('CONTACT_PANEL.CALL_CONTACT')"
      size="sm"
      variant="ghost"
      color="slate"
      icon="i-lucide-phone-call"
      class="rounded-md hover:bg-n-alpha-2"
      @click="onClickToCall"
    >
      {{ $t('CONTACT_PANEL.CALL') }}
    </ButtonV4>
    <div
      v-on-clickaway="() => toggleDropdown(false)"
      class="relative flex items-center group"
    >
      <ButtonV4
        v-tooltip="$t('CONVERSATION.HEADER.MORE_ACTIONS')"
        size="sm"
        variant="ghost"
        color="slate"
        icon="i-lucide-more-vertical"
        class="rounded-md group-hover:bg-n-alpha-2"
        @click="toggleDropdown()"
      />
      <DropdownMenu
        v-if="showActionsDropdown"
        :menu-items="actionMenuItems"
        class="mt-1 ltr:right-0 rtl:left-0 top-full"
        @action="handleActionClick"
      />
    </div>
    <EmailTranscriptModal
      v-if="showEmailActionsModal"
      :show="showEmailActionsModal"
      :current-chat="currentChat"
      @cancel="toggleEmailModal"
    />
  </div>
</template>
